import {
  Box,
  Button,
  List,
  ListItem,
  Typography
} from '@mui/material'
import { ConfirmPopup } from 'components/inlineConfirmPopup'
import { theme } from 'config'
import { observer } from 'mobx-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Container, Draggable } from 'react-smooth-dnd'
import styled from 'styled-components'
import 'suneditor/dist/css/suneditor.min.css'
import { useClickOutside } from 'util/util'
import { Icon } from '../../components/icon'
import { RichTextEditor } from '../../components/richtTextEditor'
import { Course } from '../../models'
import { AddThumbnail } from './addThumbnail'
import { handleAttachments } from './util'


export interface IStepEditorProps {
  course: Course
  noTitle?: boolean
}

export const StepEditor = observer((props: IStepEditorProps) => {
  const { t } = useTranslation();
  const { course, noTitle } = props;

  const [openConfirmPopup, setOpenConfirmPopup] = React.useState<number>(-1);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useClickOutside(wrapperRef, () => setOpenConfirmPopup(-1));

  if (!course?.getCurStep) return null;

  return (
    <>
      <Box
        className      = 'stepEditor'
        display        = 'flex'
        flexDirection  = 'column'
        justifyContent = 'center'
        pt             = {3}
      >
        {noTitle !== true && (
          <Typography
            textAlign='center'
            variant='h4'
            marginBottom={theme.spacing(3)}
          >
            {course.getCurStep.title}
          </Typography>
        )}
        <StyledList sx={{ margin: 'auto', width: '80rem' }}>
          <Container
            dragHandleSelector='.drag-handle'
            lockAxis='y'
            onDrop={(props: { removedIndex: number; addedIndex: number }) => {
              course.getCurStep.rearrangeBlocks(
                props.removedIndex,
                props.addedIndex
              )
            }}
          >
            {course.getCurStep?.getParent?.isGrid && !course.getCurStep.isVideo && course.getCurStep.is_sub &&
              <ListItem>
                <AddThumbnail course={course} />
              </ListItem>
            }
            {course.getCurStep.getBlocks.map((b, idx) => (
              <Draggable key={course.getCurStep.block_ids[idx]}>
                <ListItem>
                  <ListItemInner>
                    <DragIcon iconId='drag-handle' className='drag-handle' />
                    <RichTextEditor
                      autoFocus    = {false}
                      value        = {b}
                      defaultValue = {b}
                      onChange     = {(content: string) =>
                        course.getCurStep.updateBlock(idx, content)
                      }
                      onImageUploadBefore={(files, info, uploadHandler) =>
                        handleAttachments(course, files, info, uploadHandler)
                      }
                    />
                    <Box ref={wrapperRef}>
                      <ConfirmPopup
                        placement = 'right'
                        onClose   = {() => setOpenConfirmPopup(-1)}
                        open      = {openConfirmPopup === idx}
                        onConfirm = {() => course.getCurStep.removeBlock(idx)}
                        onCancel  = {() => setOpenConfirmPopup(-1)}
                      >
                        <DeleteIcon
                          onClick={() => {
                            setOpenConfirmPopup(idx)
                          }}
                          iconId='bin'
                        />
                      </ConfirmPopup>
                    </Box>
                  </ListItemInner>
                </ListItem>
              </Draggable>
            ))}
          </Container>
        </StyledList>
        <Box
          sx={{
            alignItems:     'center',
            display:        'flex',
            justifyContent: 'center',
            mb:             '2rem',
            mt:             '2rem',
          }}
        >
          <Button
            className='add-new-block'
            color     = 'secondary'
            variant   = 'outlined'
            startIcon = {<Icon iconId='add' />}
            onClick   = {() => course.getCurStep.addBlock()}
          >
          {t("editor_add_new_block")}
          </Button>
        </Box>
      </Box>
    </>
  )
});

const ListItemInner = styled.div`
  display:     flex;
  align-items: center;
  flex:        1;
  gap:         1.5rem;
  width:       '100%';
`

const DeleteIcon = styled(Icon)`
  transition: opacity 0.2s ease;
  cursor:     pointer;
`

const DragIcon = styled(DeleteIcon)`
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`

const StyledList = styled(List)`
  .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
    overflow: visible;
  }
`
